<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useEditUser, CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkMembershipDeletePermission } from '@ilteducation/customer-mgmt-permissions';

import useToast from '../../../composables/useToast';
import { useLocale } from '../../../composables/useLocale';
import useModal, { modals } from '../../../composables/useModal';
import { useGotoProfile } from '../../../composables/useGotoProfile';
import { useSidebarGroup, Member } from '../../../composables/useSidebarGroup';
import { useCurrentOrganization } from '../../../composables/useCurrentOrganization';

import MemberChip from './MemberChip.vue';
import DeleteMembershipsModal from '../DeleteMembershipsModal.vue';
import ManageGroupMembersModal from './ManageGroupMembersModal.vue';
import SidePanelSection from '../../SidePanel/SidePanelSection.vue';
import { usePermissions } from '../../../composables/usePermissions';
import { useAppEvents } from '../../../events/appEvents';
const { t } = useLocale();
const { toast } = useToast();

const { organization } = useCurrentOrganization();
const { getMembers, members, isMembersLoading } = useSidebarGroup();
const { deleteMembership, isLoading: isDeletingMember } = useEditUser();
const { gotoUserProfile } = useGotoProfile();
const { permissionContext, reporterWithReason, createGetTargetAssociation, getCallerAssociation, getGroupMemberships } =
  usePermissions();

const { open: openConfirmDeleteMembershipModal, close: closeConfirmDeleteMembershipModal } = useModal(
  modals.CONFIRM_DELETE_MEMBERSHIP_FROM_GROUP,
);
const { open: openManageGroupMembersModal } = useModal(modals.MANAGE_GROUP_MEMBERS);
const { emitAppEvent } = useAppEvents();

const props = defineProps<{
  loading?: boolean;
  group: CustomerMgmtTypes.GetGroupResponse;
}>();

const visibleMembersLimit = 3;
const isAllMembersVisible = ref<boolean>(false);
const visibleMembers = computed(() => {
  if (isAllMembersVisible.value) {
    return members.value ?? [];
  }

  return members.value?.slice(0, visibleMembersLimit);
});

const toggleShowAllMembers = () => {
  isAllMembersVisible.value = !isAllMembersVisible.value;
};

const showVisibleMembersExpand = computed(() => {
  return (members.value?.length ?? 0) > visibleMembersLimit;
});

const memberToDelete = ref<CustomerMgmtTypes.MembershipOutput | null>(null);

const onDeleteMember = (member: CustomerMgmtTypes.MembershipOutput) => {
  memberToDelete.value = member;
  openConfirmDeleteMembershipModal();
};

const onRejectDeleteMember = () => {
  memberToDelete.value = null;
  closeConfirmDeleteMembershipModal();
};

const onConfirmDeleteMember = async () => {
  if (memberToDelete.value) {
    const success = await deleteMembership(memberToDelete.value.user.uid, props.group.uid);
    if (success) {
      await getMembers(props.group.uid, organization.value.uid);
      emitAppEvent({ type: 'membership:updated', organizationUid: organization.value.uid });
      closeConfirmDeleteMembershipModal();
      toast(t('lcp.member_removed_successfully'), 'success');
    }
  }
};

const isAllowedToDelete = (member: Member) => {
  const permissionError = checkMembershipDeletePermission(
    permissionContext.value,
    {
      ...member,
      group: {
        ...member.group,
        organization: member.group.schoolUnit,
      },
    },
    getCallerAssociation,
    createGetTargetAssociation(member.user),
    getGroupMemberships,
    reporterWithReason,
  );
  return permissionError;
};

const title = computed(() => {
  return t('lcp.manage_members') + (members.value?.length ? ` (${members.value.length})` : '');
});

const isMemberDeletableByUid = ref<Record<string, ReturnType<typeof reporterWithReason>>>({});
watch(
  () => members.value,
  async (membersList) => {
    if (membersList) {
      membersList.forEach(async (member) => {
        isMemberDeletableByUid.value[member.user.uid] = await isAllowedToDelete(member);
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <SidePanelSection
    :title="title"
    :action="openManageGroupMembersModal"
    class="w-100"
    :loading="loading || isMembersLoading"
  >
    <v-row v-if="isMembersLoading" no-gutters>
      <v-col>
        <v-skeleton-loader v-if="isMembersLoading" type="paragraph"></v-skeleton-loader>
      </v-col>
    </v-row>
    <v-row v-else class="ga-2" no-gutters>
      <MemberChip
        v-for="member in visibleMembers"
        :key="member.user.uid"
        :user-name="member.user.name"
        :permission-error="
          isMemberDeletableByUid[member.user.uid]?.allowed ? false : isMemberDeletableByUid[member.user.uid]?.reason
        "
        @delete="() => onDeleteMember(member)"
        @goto-user="() => gotoUserProfile(member.user.uid)"
      />
      <v-chip v-if="showVisibleMembersExpand && !isAllMembersVisible">...</v-chip>
    </v-row>

    <v-btn v-if="showVisibleMembersExpand" variant="text" @click="toggleShowAllMembers">
      {{ isAllMembersVisible ? t('lcp.show_less') : t('lcp.show_more') }}
    </v-btn>
  </SidePanelSection>
  <ManageGroupMembersModal
    v-if="props.group"
    :name="modals.MANAGE_GROUP_MEMBERS"
    :group="props.group"
    :members="members ?? []"
    :organization-uid="organization?.uid"
  />

  <DeleteMembershipsModal
    v-if="props.group"
    :name="modals.CONFIRM_DELETE_MEMBERSHIP_FROM_GROUP"
    :onConfirm="onConfirmDeleteMember"
    :onReject="onRejectDeleteMember"
    :is-loading="isDeletingMember || isMembersLoading"
  />
</template>
